import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { useWorld } from "@/context/WorldContext";
import { isReviewer, ago } from "@/lib/perms";
import { pageDoc } from "@/lib/blocks";
import { DiffView } from "@/components/PageHistory";

const docOf = (p) => (p ? [`# ${p.title}`, `Category: ${p.category}`, p.summary, ...(p.sections || []).flatMap((s) => s.facts.map((f) => `${s.title} · ${f.key}: ${f.items.map((i) => i.text).join(", ")}`)),
  pageDoc(p), (p.tags || []).length ? `Tags: ${p.tags.join(", ")}` : ""].filter(Boolean).join("\n") : "");
const ACTION = { create: "New entry", update: "Edit", delete: "Deletion" };
const NewDoc = ({ text }) => (
  <pre className="font-code text-[11px] leading-relaxed whitespace-pre-wrap rounded-lg bg-canvas border border-line p-3 max-h-[45vh] overflow-y-auto text-emerald-300" data-testid="new-entry-preview">{text}</pre>
);

const Detail = ({ id, reviewer, onDone }) => {
  const [c, setC] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setC(null); api.get(`/changes/${id}`).then((r) => setC(r.data)); }, [id]);
  if (!c) return <Loader2 className="w-5 h-5 animate-spin text-amber-500" />;
  const act = async (what, force = false) => {
    if (what === "accept" && c.conflict && !force && !window.confirm("The live entry changed after this was submitted. Accept anyway?")) return;
    setBusy(true);
    try { await api.post(`/changes/${id}/${what}`, { note, force: force || c.conflict }); toast.success(what === "accept" ? "Change published" : "Change rejected"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="change-detail">
      <div>
        <p className="font-code text-[11px] uppercase tracking-widest text-amber-500/80">{c.kind === "suggestion" ? "Suggestion" : ACTION[c.action]} · {c.author_name} · {ago(c.created_at)}</p>
        <h2 className="font-display text-3xl text-amber-50">{c.title}</h2>
        {c.message && <p className="mt-2 text-sm text-slate-300 italic">"{c.message}"</p>}
      </div>
      {c.conflict && <p className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200" data-testid="change-conflict"><AlertTriangle className="w-4 h-4" /> The live entry changed after this was submitted. Check the comparison before accepting.</p>}
      <div className="grid grid-cols-2 text-[11px] font-code uppercase tracking-widest text-slate-500">
        <span>{c.action === "create" ? "New entry" : "− Live"}</span><span className="text-right">{c.action === "delete" ? "Removed" : "+ Proposed"}</span>
      </div>
      {c.action === "create" ? <NewDoc text={docOf(c.data)} /> : <DiffView from={docOf(c.live)} to={c.action === "delete" ? "" : docOf(c.data)} />}
      {reviewer && c.status === "pending" && (
        <div className="space-y-3">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note to the author" className="bg-inset border-line" data-testid="change-note" />
          <div className="flex gap-3">
            <button disabled={busy} onClick={() => act("accept")} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-onaccent font-semibold h-11" data-testid="change-accept-btn"><Check className="w-4 h-4" /> Accept</button>
            <button disabled={busy} onClick={() => act("reject")} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-rose-500/50 text-rose-300 hover:bg-rose-500/10 h-11" data-testid="change-reject-btn"><X className="w-4 h-4" /> Reject</button>
          </div>
        </div>
      )}
      {!reviewer && <p className="text-xs text-slate-500">Waiting for the owner or an admin to review.</p>}
    </div>
  );
};

export default function PendingChanges() {
  const { world, basePath, reload } = useWorld();
  const [sp, setSp] = useSearchParams();
  const [list, setList] = useState(null);
  const sel = sp.get("c");
  const load = useCallback(() => api.get(`/worlds/${world.id}/changes`).then((r) => setList(r.data)), [world.id]);
  useEffect(() => { load(); }, [load]);
  const reviewer = isReviewer(world);
  const done = () => { setSp({}); load(); reload(); };
  return (
    <div className="px-6 md:px-10 py-10" data-testid="pending-changes-page">
      <p className="font-code text-xs uppercase tracking-[0.3em] text-amber-500/80 mb-2">Review</p>
      <h1 className="font-display text-4xl sm:text-5xl text-amber-50 mb-2">Pending changes</h1>
      <p className="text-sm text-slate-400 mb-8">{reviewer ? "Accept to publish, or reject with a note." : "Your changes and suggestions waiting for review."}</p>
      <div className="grid lg:grid-cols-[360px_minmax(0,1fr)] gap-8">
        <ul className="space-y-2" data-testid="pending-list">
          {list === null ? <Loader2 className="w-5 h-5 animate-spin text-amber-500" /> : list.length === 0 ? <p className="text-sm text-slate-500" data-testid="pending-empty">Nothing waiting. The codex is up to date.</p> : list.map((c) => (
            <li key={c.id}>
              <button onClick={() => setSp({ c: c.id })} className={`w-full text-left rounded-xl border p-4 transition-colors ${sel === c.id ? "border-amber-500/60 bg-amber-500/5" : "border-line bg-panel hover:border-amber-500/30"}`} data-testid={`pending-item-${c.id}`}>
                <p className="text-[11px] font-code uppercase tracking-widest text-slate-500">{c.kind === "suggestion" ? "Suggestion" : ACTION[c.action]} · {c.category}{c.conflict ? " · conflict" : ""}</p>
                <p className="font-display text-xl text-amber-100">{c.title}</p>
                <p className="text-xs text-slate-500">{c.author_name} · {ago(c.updated_at)}</p>
              </button>
            </li>
          ))}
        </ul>
        <div className="min-w-0">{sel ? <Detail id={sel} reviewer={reviewer} onDone={done} /> : <p className="text-sm text-slate-500">Pick a change to compare it with the live page. <Link to={`${basePath}/activity`} className="text-amber-300 hover:underline">See activity</Link></p>}</div>
      </div>
    </div>
  );
}
